import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, ShieldAlert, Target, Sparkles } from "lucide-react";

const testId = {
  generate: "predictor-generate",
};

function Gauge({ label, value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const color = pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-red-500";
  const stroke = pct >= 70 ? "#059669" : pct >= 40 ? "#d97706" : "#ef4444";
  const circumference = 2 * Math.PI * 52;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <div className="card-flat text-center">
      <div className="label-mono">{label}</div>
      <div className="relative mx-auto mt-3 h-32 w-32">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r="52" stroke="#e7e5e4" strokeWidth="10" fill="none" />
          <circle cx="60" cy="60" r="52" stroke={stroke} strokeWidth="10" fill="none"
                  strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                  className="transition-all duration-700" />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center font-display text-3xl font-bold ${color}`}>
          {Math.round(pct)}<span className="text-base">%</span>
        </div>
      </div>
    </div>
  );
}

export default function SuccessPredictor() {
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);

  const load = async () => {
    const { data } = await api.get("/ai/success-predictor/latest");
    if (data.prediction) setPrediction(data.prediction.prediction);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/success-predictor");
      setPrediction(data.prediction);
      toast.success("Success Predictor updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono mb-2">AI Success Predictor™</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Your path to success — quantified.</h1>
          <p className="mt-2 text-sm text-stone-600">Probabilities of success, funding, and survival based on your Business DNA and health signals.</p>
        </div>
        <button data-testid={testId.generate} onClick={generate} disabled={loading} className="btn-primary">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</> : <><Sparkles className="h-4 w-4" /> Run Prediction</>}
        </button>
      </div>

      {prediction && prediction.success_probability !== undefined && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Gauge label="Probability of Success (24mo)" value={prediction.success_probability} />
            <Gauge label="Probability of Funding (12mo)" value={prediction.funding_probability} />
            <Gauge label="Probability of Survival (36mo)" value={prediction.survival_probability} />
          </div>

          {prediction.reasoning && (
            <div className="card-flat">
              <div className="label-mono mb-2">AI Reasoning</div>
              {typeof prediction.reasoning === "string" ? (
                <p className="text-sm leading-relaxed text-slate-950">{prediction.reasoning}</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(prediction.reasoning).map(([k, v]) => (
                    <div key={k} className="border-l-2 border-slate-950 pl-4">
                      <div className="label-mono !text-[10px] mb-1">{k.replace(/_/g, " ")}</div>
                      <p className="text-sm leading-relaxed text-slate-950">{typeof v === "string" ? v : JSON.stringify(v)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            <div className="card-flat">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-500" />
                <div className="label-mono">Top Risks</div>
              </div>
              <div className="mt-4 space-y-3">
                {(prediction.top_risks || []).map((r, i) => (
                  <div key={i} className="border-l-2 border-red-500 pl-4">
                    <div className="text-sm font-medium text-slate-950">{r.risk}</div>
                    <div className="mt-1 text-xs leading-relaxed text-stone-600"><span className="font-medium">Mitigation:</span> {r.mitigation}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-flat">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-emerald-600" />
                <div className="label-mono">Next Best Actions</div>
              </div>
              <div className="mt-4 space-y-3">
                {(prediction.next_best_actions || []).map((a, i) => (
                  <div key={i} className="border-l-2 border-emerald-500 pl-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-slate-950">{a.action}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">{a.timeframe}</div>
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-stone-600">{a.why}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {!prediction && (
        <div className="card-flat py-16 text-center">
          <TrendingUp className="mx-auto h-10 w-10 text-stone-300" />
          <div className="mt-4 font-display text-xl font-medium text-slate-950">No prediction yet</div>
          <p className="mt-2 text-sm text-stone-600">Click "Run Prediction" to get your Success Predictor report.</p>
        </div>
      )}
    </div>
  );
}
